// This file is a wrapper around @testing-library/react that provides a custom render function
// with test providers (QueryClientProvider, LocaleInfoProvider).
// The original render function is exported as renderWithoutProviders for testing error cases.
// All other exports are re-exported as-is.
// Import from '@test-utils' instead of @testing-library/react in your tests.
// Please refer to the TYPESCRIPT_TESTING.md file for more information on how to use the test utils.

/* eslint-disable no-restricted-imports, import/export */
import { ReactElement, ReactNode, useMemo } from 'react';
import { render as rtlRender, RenderOptions } from '@testing-library/react';
import {
  QueryClient,
  QueryClientProvider,
  QueryCache,
  MutationCache,
} from '@tanstack/react-query';

import { CountryCode, LanguageCode } from 'typescript/constants/localization';
import { LocaleInfoProvider } from 'typescript/context/locale_info';
// Re-export everything from @testing-library/react
export * from '@testing-library/react';
export * from '@testing-library/user-event';

// Export the raw render without any providers for testing error cases
export { render as renderWithoutProviders } from '@testing-library/react';

/* eslint-enable no-restricted-imports, import/export */

// Create a default QueryClient for tests - mimics CustomQueryClientProvider but without Sentry logging
const createTestQueryClient = () =>
  new QueryClient({
    queryCache: new QueryCache(),
    mutationCache: new MutationCache(),
    defaultOptions: {
      queries: {
        retry: false, // Disable retries in tests for faster execution
        gcTime: Number.POSITIVE_INFINITY, // Keep data in cache to avoid garbage collection during tests
      },
      mutations: {
        retry: false, // Disable retries in tests
      },
    },
  });

// Test provider wrapper that includes common providers for testing
// Includes: QueryClientProvider, LocaleInfoProvider (defaults to US/EN)
// If you need to mock useLocaleInfo, use jest.spyOn() - see usage docs above
export const TestProviderWrapper = ({
  children,
  queryClient,
  countryCode = CountryCode.US,
  languageCode = LanguageCode.EN,
}: {
  children: ReactNode;
  queryClient?: QueryClient;
  countryCode?: CountryCode;
  languageCode?: LanguageCode;
}) => {
  // Create a default query client if none is provided
  // Use useMemo to ensure stable client instance across re-renders
  const client = useMemo(
    () => queryClient || createTestQueryClient(),
    [queryClient],
  );

  return (
    <QueryClientProvider client={client}>
      <LocaleInfoProvider countryCode={countryCode} languageCode={languageCode}>
        {children}
      </LocaleInfoProvider>
    </QueryClientProvider>
  );
};

// Custom render function that wraps components with test providers
// This overrides the render function exported from @testing-library/react above
// eslint-disable-next-line import/export
export function render(
  ui: ReactElement,
  options?: Omit<RenderOptions, 'wrapper'> & {
    queryClient?: QueryClient;
    countryCode?: CountryCode;
    languageCode?: LanguageCode;
  },
) {
  const { queryClient, countryCode, languageCode, ...renderOptions } =
    options || {};

  const Wrapper = ({ children }: { children: ReactNode }) => (
    <TestProviderWrapper
      queryClient={queryClient}
      countryCode={countryCode}
      languageCode={languageCode}
    >
      {children}
    </TestProviderWrapper>
  );

  return rtlRender(ui, { wrapper: Wrapper, ...renderOptions });
}
